'use client';

// TODO: wire to customer_pipeline.stage transitions for Bid-Hit and Sold/Started;
// wire to instant_estimate counts for Proposal Aging. Lead-source mix will
// likely pull from customer_pipeline.referral_source aggregated over a window.

import Icon from '@/components/ui/AppIcon';
import DemoDataPill from './DemoDataPill';

// ── Mock metrics ───────────────────────────────────────────────────────────

const BID_HIT       = { value: '0%',  delta: '', tone: 'up'   as const };
const PROPOSAL_AGE  = { avgDays: '0d', overdueCount: 0 };
const SOLD_STARTED  = { sold: 0, started: 0 };
const LEAD_SOURCES: Array<{ label: string; pct: number; color: string }> = [];

// ── Component ──────────────────────────────────────────────────────────────

export default function Row6SalesHandoff() {
  return (
    <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {/* Bid-Hit Ratio */}
      <Card title="Bid-Hit Ratio" iconName="TrophyIcon" iconColor="#10B981">
        <p className="text-3xl font-bold leading-tight" style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}>
          {BID_HIT.value}
        </p>
        <p className="text-xs font-medium mt-1" style={{ color: '#10B981' }}>
          <span className="font-bold mr-1">▲</span>{BID_HIT.delta}
        </p>
      </Card>

      {/* Proposal Aging */}
      <Card title="Proposal Aging" iconName="ClockIcon" iconColor="#F59E0B">
        <p className="text-3xl font-bold leading-tight" style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}>
          {PROPOSAL_AGE.avgDays}
        </p>
        <p className="text-xs text-gray-500 mt-1">avg open</p>
        <p className="text-[11px] font-medium mt-1" style={{ color: '#EF4444' }}>
          {PROPOSAL_AGE.overdueCount} proposals &gt; 14 days
        </p>
      </Card>

      {/* Sold vs Started */}
      <Card title="Sold / Started" subtitle="This week" iconName="ArrowsRightLeftIcon" iconColor="#07B0E9">
        <div className="flex items-baseline gap-3">
          <div>
            <p className="text-3xl font-bold leading-tight" style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}>
              {SOLD_STARTED.sold}
            </p>
            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Sold</p>
          </div>
          <span className="text-2xl text-gray-300">/</span>
          <div>
            <p className="text-3xl font-bold leading-tight" style={{ color: '#033494', fontFamily: 'Inter, Poppins, sans-serif' }}>
              {SOLD_STARTED.started}
            </p>
            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Started</p>
          </div>
        </div>
      </Card>

      {/* Lead Source Mix */}
      <Card title="Lead Source Mix" iconName="ChartPieIcon" iconColor="#8B5CF6">
        <div className="flex items-stretch gap-0.5 h-3 rounded-full overflow-hidden mt-1">
          {LEAD_SOURCES.map((s) => (
            <div key={s.label} style={{ background: s.color, width: `${s.pct}%` }} title={`${s.label}: ${s.pct}%`} />
          ))}
        </div>
        <div className="grid grid-cols-2 gap-x-2 gap-y-1 mt-2.5">
          {LEAD_SOURCES.map((s) => (
            <div key={s.label} className="flex items-center gap-1.5 text-[11px]">
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: s.color }} />
              <span className="text-gray-700">{s.label}</span>
              <span className="ml-auto text-gray-500 font-medium">{s.pct}%</span>
            </div>
          ))}
        </div>
      </Card>
    </section>
  );
}

function Card({
  title,
  subtitle,
  iconName,
  iconColor,
  children,
}: {
  title: string;
  subtitle?: string;
  iconName: string;
  iconColor: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className="relative rounded-xl p-5"
      style={{ background: '#FFFFFF', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #F0F0F0' }}
    >
      <DemoDataPill />
      <div className="flex items-center gap-2 mb-2">
        <Icon name={iconName} size={14} style={{ color: iconColor }} />
        <p className="text-[11px] uppercase tracking-wider font-semibold text-gray-500">{title}</p>
      </div>
      {subtitle && <p className="text-[10px] text-gray-400 mb-1">{subtitle}</p>}
      {children}
    </div>
  );
}
